import React, {useEffect, useState} from 'react';
import {
    Alert,
    App,
    Button,
    Form,
    Input,
    List,
    Modal,
    Popconfirm,
    Select,
    Space,
    Steps,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {
    ApiOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    ReloadOutlined,
    SyncOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import {newsService} from '../../services/newsService';
import NewsSourceNewsModal from './NewsSourceNewsModal';

const {Option} = Select;
const {Text} = Typography;

interface NewsSource {
    id: number;
    name: string;
    rssUrl: string;
    category: 'CRYPTO' | 'FOREX' | 'MACRO' | 'STOCK';
    language: string;
    priority: number;
    enabled: boolean;
    lastFetchTime?: string;
    lastError?: string;
    createdAt: string;
}

const NewsManagement: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const [sources, setSources] = useState<NewsSource[]>([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingSource, setEditingSource] = useState<NewsSource | null>(null);
    const [form] = Form.useForm();

    // 分步表单状态
    const [currentStep, setCurrentStep] = useState(0);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<any>(null);

    const [stats, setStats] = useState({
        totalSources: 0,
        enabledSources: 0,
        totalNews: 0
    });

    // 浏览弹窗状态
    const [newsModalVisible, setNewsModalVisible] = useState(false);
    const [viewingSource, setViewingSource] = useState<{ id: number; name: string } | null>(null);
    const [fetchingSource, setFetchingSource] = useState<number | null>(null);

    useEffect(() => {
        loadSources();
        loadStats();
    }, []);

    const loadSources = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/v1/news/sources?page=0&size=100');
            setSources(response.data.data?.content || []);
        } catch (error) {
            messageApi.error('获取RSS源列表失败');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const newsResponse = await api.get('/api/v1/news?page=0&size=1');
            const sourcesResponse = await api.get('/api/v1/news/sources?page=0&size=100');
            const sourcesList = sourcesResponse.data.data?.content || [];

            setStats({
                totalSources: sourcesResponse.data.data?.totalElements || 0,
                enabledSources: sourcesList.filter((s: NewsSource) => s.enabled).length,
                totalNews: newsResponse.data.data?.totalElements || 0
            });
        } catch (error) {
            // ignore
        }
    };

    const handleCreate = () => {
        setEditingSource(null);
        setCurrentStep(0);
        setTestResult(null);
        setModalVisible(true);
        form.resetFields();
        form.setFieldsValue({
            name: '',
            rssUrl: '',
            category: 'CRYPTO',
            language: 'zh',
            priority: 0,
            enabled: true
        });
    };

    const handleEdit = (source: NewsSource) => {
        setEditingSource(source);
        setCurrentStep(0);
        setTestResult(null);
        setModalVisible(true);
        form.setFieldsValue(source);
    };

    const handleTest = async () => {
        try {
            const values = await form.validateFields(['name', 'rssUrl', 'category']);
            setTesting(true);
            setTestResult(null);

            const response = await api.post('/api/v1/news/sources/test', {rssUrl: values.rssUrl});
            setTestResult(response.data.data);

            if (response.data.data?.success) {
                setCurrentStep(1);
            }
        } catch (error: any) {
            setTestResult({success: false, error: error.response?.data?.message || '测试失败'});
        } finally {
            setTesting(false);
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            if (editingSource) {
                await api.put(`/api/v1/news/sources/${editingSource.id}`, values);
                messageApi.success('更新成功');
            } else {
                await api.post('/api/v1/news/sources', values);
                messageApi.success('添加成功');
            }

            setModalVisible(false);
            loadSources();
            loadStats();
        } catch (error) {
            messageApi.error('保存失败');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await api.delete(`/api/v1/news/sources/${id}`);
            messageApi.success('删除成功');
            loadSources();
            loadStats();
        } catch (error) {
            messageApi.error('删除失败');
        }
    };

    const handleToggleEnabled = async (source: NewsSource) => {
        try {
            await api.put(`/api/v1/news/sources/${source.id}`, {
                ...source,
                enabled: !source.enabled
            });
            messageApi.success(source.enabled ? '已禁用' : '已启用');
            loadSources();
            loadStats();
        } catch (error) {
            messageApi.error('操作失败');
        }
    };

    const handleFetch = async () => {
        setFetching(true);
        try {
            await api.post('/api/v1/news/fetch');
            messageApi.success('RSS抓取任务已触发');
            loadSources();
            loadStats();
        } catch (error) {
            messageApi.error('触发抓取失败');
        } finally {
            setFetching(false);
        }
    };

    // 立即抓取指定RSS源
    const handleFetchSource = async (sourceId: number, sourceName: string) => {
        setFetchingSource(sourceId);
        try {
            await newsService.fetchSourceById(sourceId);
            messageApi.success(`RSS源【${sourceName}】抓取任务已触发`);
            setTimeout(() => {
                loadSources();
                loadStats();
            }, 2000);
        } catch (error) {
            messageApi.error('触发抓取失败');
        } finally {
            setFetchingSource(null);
        }
    };

    // 浏览RSS源的新闻
    const handleViewNews = (source: NewsSource) => {
        setViewingSource({id: source.id, name: source.name});
        setNewsModalVisible(true);
    };

    const getCategoryLabel = (category: string) => {
        const map: Record<string, string> = {
            CRYPTO: '加密货币',
            FOREX: '外汇',
            MACRO: '宏观经济',
            STOCK: '股票'
        };
        return map[category] || category;
    };

    const getCategoryColor = (category: string) => {
        const map: Record<string, string> = {
            CRYPTO: 'orange',
            FOREX: 'blue',
            MACRO: 'green',
            STOCK: 'purple'
        };
        return map[category] || 'default';
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60
        },
        {
            title: '名称',
            dataIndex: 'name',
            width: 150
        },
        {
            title: '分类',
            dataIndex: 'category',
            width: 100,
            render: (category: string) => (
                <Tag color={getCategoryColor(category)}>{getCategoryLabel(category)}</Tag>
            )
        },
        {
            title: '状态',
            dataIndex: 'enabled',
            width: 60,
            align: 'center' as const,
            render: (enabled: boolean) => (
                enabled
                    ? <CheckCircleOutlined style={{color: 'var(--pt-bull)', fontSize: 16}}/>
                    : <CloseCircleOutlined style={{color: 'var(--pt-bear)', fontSize: 16}}/>
            )
        },
        {
            title: '最后抓取',
            dataIndex: 'lastFetchTime',
            width: 150,
            render: (time: string) => {
                if (!time) return '-';
                const date = dayjs(time);
                return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : '-';
            }
        },
        {
            title: '操作',
            width: 180,
            render: (_: any, record: NewsSource) => (
                <Space size="small">
                    <Tooltip title="立即抓取">
                        <Button
                            size="small"
                            type="text"
                            icon={<SyncOutlined spin={fetchingSource === record.id}/>}
                            onClick={() => handleFetchSource(record.id, record.name)}
                            loading={fetchingSource === record.id}
                        />
                    </Tooltip>
                    <Tooltip title="浏览">
                        <Button size="small" type="text" icon={<EyeOutlined/>} onClick={() => handleViewNews(record)}/>
                    </Tooltip>
                    <Tooltip title="编辑">
                        <Button size="small" type="text" icon={<EditOutlined/>} onClick={() => handleEdit(record)}/>
                    </Tooltip>
                    <Switch
                        size="small"
                        checked={record.enabled}
                        onChange={() => handleToggleEnabled(record)}
                    />
                    <Popconfirm
                        title="确定删除该RSS源？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button size="small" type="text" danger icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{padding: '16px'}}>
            {/* 顶部工具栏：统计 + 操作 */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
                padding: '12px 16px',
                background: 'rgb(12, 17, 32)',
                borderRadius: 6
            }}>
                {/* 统计信息 */}
                <Space size={24}>
          <span>
            <Text type="secondary">RSS源</Text>
            <Text strong style={{marginLeft: 8, fontSize: 16}}>{stats.totalSources}</Text>
          </span>
                    <span>
            <Text type="secondary">已启用</Text>
            <Text strong style={{marginLeft: 8, fontSize: 16, color: 'var(--pt-bull)'}}>{stats.enabledSources}</Text>
          </span>
                    <span>
            <Text type="secondary">新闻</Text>
            <Text strong style={{marginLeft: 8, fontSize: 16}}>{stats.totalNews}</Text>
          </span>
                </Space>

                {/* 操作按钮 */}
                <Space>
                    <Button type="primary" icon={<PlusOutlined/>} onClick={handleCreate}>
                        {/*添加*/}
                    </Button>
                    <Button
                        icon={<SyncOutlined spin={fetching}/>}
                        onClick={handleFetch}
                        loading={fetching}
                    >
                        {/*抓取*/}
                    </Button>
                    <Button icon={<ReloadOutlined/>} onClick={() => {
                        loadSources();
                        loadStats();
                    }}>
                        {/*刷新*/}
                    </Button>
                </Space>
            </div>

            {/* RSS源列表 */}
            <Table
                columns={columns}
                dataSource={sources}
                rowKey="id"
                loading={loading}
                pagination={{pageSize: 10}}
                size="small"
            />

            {/* 添加/编辑弹窗 - 分步表单 */}
            <Modal
                title={editingSource ? '编辑RSS源' : '添加RSS源'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Steps
                    current={currentStep}
                    size="small"
                    style={{marginBottom: 24}}
                    items={[
                        {title: '填写信息'},
                        {title: '测试验证'}
                    ]}
                />

                {/* Step 0: 填写基本信息 */}
                <Form form={form} layout="vertical" style={{display: currentStep === 0 ? 'block' : 'none'}}>
                    <Form.Item
                        name="name"
                        label="名称"
                        rules={[{required: true, message: '请输入名称'}]}
                    >
                        <Input placeholder="如: CoinDesk"/>
                    </Form.Item>

                    <Form.Item
                        name="rssUrl"
                        label="RSS地址"
                        rules={[
                            {required: true, message: '请输入RSS地址'},
                            {type: 'url', message: '请输入有效的URL'}
                        ]}
                    >
                        <Input placeholder="https://example.com/feed"/>
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label="分类"
                        rules={[{required: true, message: '请选择分类'}]}
                    >
                        <Select>
                            <Option value="CRYPTO">加密货币</Option>
                            <Option value="FOREX">外汇</Option>
                            <Option value="MACRO">宏观经济</Option>
                            <Option value="STOCK">股票</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="language" label="语言">
                        <Select>
                            <Option value="zh">中文</Option>
                            <Option value="en">英文</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="priority" label="优先级" extra="数值越大优先级越高">
                        <Input type="number"/>
                    </Form.Item>

                    <Form.Item name="enabled" label="状态" valuePropName="checked">
                        <Switch checkedChildren="启用" unCheckedChildren="禁用"/>
                    </Form.Item>

                    <div style={{textAlign: 'right', marginTop: 16}}>
                        <Button onClick={() => setModalVisible(false)} style={{marginRight: 8}}>
                            取消
                        </Button>
                        <Button type="primary" icon={<ApiOutlined/>} onClick={handleTest} loading={testing}>
                            测试RSS源
                        </Button>
                    </div>
                </Form>

                {/* Step 1: 测试结果 */}
                {currentStep === 1 && (
                    <div>
                        {testResult?.success ? (
                            <div>
                                <Alert
                                    type="success"
                                    showIcon
                                    message={`RSS源测试成功，获取到 ${testResult.count} 条新闻`}
                                    style={{marginBottom: 16}}
                                />

                                {testResult.preview?.length > 0 && (
                                    <div>
                                        <Text type="secondary">最新新闻预览：</Text>
                                        <List
                                            size="small"
                                            dataSource={testResult.preview}
                                            renderItem={(item: any) => (
                                                <List.Item>
                                                    <Text ellipsis style={{maxWidth: 400}}>{item.title}</Text>
                                                    <Text type="secondary" style={{marginLeft: 8, fontSize: 12}}>
                                                        {item.pubDate}
                                                    </Text>
                                                </List.Item>
                                            )}
                                            style={{marginTop: 8}}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Alert
                                type="error"
                                showIcon
                                message="测试失败"
                                description={testResult?.error}
                            />
                        )}

                        <div style={{textAlign: 'right', marginTop: 24}}>
                            <Button onClick={() => {
                                setCurrentStep(0);
                                setTestResult(null);
                            }} style={{marginRight: 8}}>
                                返回修改
                            </Button>
                            {testResult?.success && (
                                <Button type="primary" onClick={handleSave}>
                                    保存
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            {/* 新闻浏览弹窗 */}
            <NewsSourceNewsModal
                visible={newsModalVisible}
                sourceId={viewingSource?.id ?? null}
                sourceName={viewingSource?.name ?? ''}
                onCancel={() => {
                    setNewsModalVisible(false);
                    setViewingSource(null);
                }}
            />
        </div>
    );
};

export default NewsManagement;
